import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, InputAdornment, Stack } from '@mui/material';
import { CalculsFormCalendrierAssistance } from '@norma/shared/constants/calculs';
import { calendrierAssistanceDefaultValues } from '@norma/shared/constants/prejudice/defaultValues/FormCalendrierAssistance.defaultValues';
import { getShouldNotDisplayCapitalisation } from '@norma/shared/helpers/prejudices/capitalisation';
import {
  PrejudiceFormCalendrierAssistance,
  PrejudiceFormCalendrierAssistanceRow,
} from '@norma/shared/types/prejudice.type';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DintilhacText } from 'src/components/client/prejudiceFormComponents/DintilhacText';
import { validationSchemaCalendrierAssistance } from 'src/constants/prejudice/validation/FormCalendrierAssistance.validation';
import { ComputedPropsForm } from '../../forms/ComputedPropsForm';
import { ExpandableTextFieldForm } from '../../forms/ExpandableTextFieldForm';
import { PrejudiceContainer } from '../PrejudiceContainer';
import { Chiffrage } from '../prejudiceFormComponents/Chiffrage';
import { DintilhacButton } from '../prejudiceFormComponents/DintilhacButton';
import { TableAssistance } from '../prejudiceFormComponents/TableAssistance/TableAssistance';
import { TotalReparti } from '../prejudiceFormComponents/TotalReparti';
import { PrejudiceFormProps } from '../PrejudiceFormProps';
import { SavePrejudiceButton } from '../SavePrejudiceButton';

interface Props extends PrejudiceFormProps<PrejudiceFormCalendrierAssistance> {
  DFTPRows: PrejudiceFormCalendrierAssistanceRow[] | null;
}

export const FormCalendrierAssistance: React.FC<Props> = ({
  victime,
  procedure,
  values,
  monetaryErosions,
  dateLiquidation,
  DFTPRows,
  allNumerosPieces,
  dateConsolidation,
  dateDeces,
  ...props
}) => {
  const defaultValues: PrejudiceFormCalendrierAssistance = useMemo(
    () =>
      calendrierAssistanceDefaultValues({
        procedure,
        values,
        isExtractedValues: false,
      }),
    [values, procedure],
  );
  const useFormReturn = useForm<PrejudiceFormCalendrierAssistance>({
    defaultValues,
    resolver: yupResolver(
      validationSchemaCalendrierAssistance({
        procedure,
        victime,
        prejudiceType: props.prejudiceType,
      }),
    ),
  });
  const { control, setValue } = useMemo(() => useFormReturn, [useFormReturn]);

  const { t } = useTranslation();
  const shouldNotDisplayCapitalisation = getShouldNotDisplayCapitalisation({
    dateConsolidation,
    dateLiquidation,
    dateDeces,
  });

  const [showDintilhac, setShowDintilhac] = React.useState<boolean>(false);

  return (
    <PrejudiceContainer<PrejudiceFormCalendrierAssistance>
      {...props}
      procedure={procedure}
      victime={victime}
      monetaryErosions={monetaryErosions}
      {...useFormReturn}
      renderPrejudice={() => {
        return (
          <Stack direction="column" spacing={4} flex={1}>
            <ExpandableTextFieldForm
              control={control}
              name="notes"
              label={t('prejudice.fields.notes.label')}
              dialogTitle={t('prejudice.fields.notes.label')}
              placeholder={t('prejudice.fields.notes.placeholder') || ''}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <DintilhacButton onClick={() => setShowDintilhac(true)} />
                  </InputAdornment>
                ),
              }}
              multiline
              maxRows={4}
              fullWidth
            />
            <DintilhacText
              open={showDintilhac}
              setOpen={setShowDintilhac}
              label={t('prejudice.fields.dintilhac.label')}
              content={i18next.t(
                `prejudice.prejudicesTypes.${props.prejudiceType}.introduction`,
                {
                  defaultValue: '',
                },
              )}
            />
            <TableAssistance
              control={control}
              tiersPayeurs={procedure.tiersPayeurs}
              hospitalisations={procedure.hospitalisations}
              dateLiquidation={dateLiquidation}
              dateConsolidation={dateConsolidation}
              monetaryErosions={monetaryErosions}
              prejudiceType={props.prejudiceType}
              procedure={procedure}
              victime={victime}
              DFTPRows={DFTPRows}
              allNumerosPieces={allNumerosPieces}
              shouldNotDisplayCapitalisation={shouldNotDisplayCapitalisation}
              dateDeces={dateDeces}
            />
            <Stack direction="row-reverse">
              <ComputedPropsForm
                control={control}
                watchFields={['rows', 'revalorisationCoefficientsType']}
                compute={([rows, revalorisationCoefficientsType]) => {
                  const totaux = CalculsFormCalendrierAssistance.totaux({
                    rows,
                    partResponsabilite: procedure.partResponsabilite,
                    tiersPayeurs: procedure.tiersPayeurs,
                    dateLiquidation,
                    monetaryErosions,
                    withDeductionFiscale: true,
                    revalorisationCoefficientsType,
                  });
                  return {
                    props: totaux,
                  };
                }}
                render={(props) => {
                  const { resteACharge, priseEnChargeTiersPayeurs } =
                    props as ReturnType<
                      typeof CalculsFormCalendrierAssistance.totaux
                    >;
                  return (
                    <TotalReparti
                      totalResteACharge={resteACharge.total.value}
                      priseEnChargeTiersPayeurs={Object.entries(
                        priseEnChargeTiersPayeurs,
                      ).reduce(
                        (
                          accumulator,
                          [
                            tiersPayeur,
                            {
                              total: { value },
                            },
                          ],
                        ) => ({
                          ...accumulator,
                          [tiersPayeur]: value,
                        }),
                        {},
                      )}
                      partResponsabilite={procedure.partResponsabilite}
                    />
                  );
                }}
              />
            </Stack>
            <Grid container>
              <Grid item xs={4}>
                <Chiffrage control={control} />
              </Grid>
              <Grid
                item
                xs={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <SavePrejudiceButton victime={victime} />
              </Grid>
              <Grid item xs={4} />
            </Grid>
          </Stack>
        );
      }}
    />
  );
};
